import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useReducer, type Dispatch } from "react";
import type {
  AppointmentPreviousSoapNoteContent,
  AppointmentPreviousSoapNoteListed,
  AppointmentPreviousSoapNotesResponse,
} from "../../../../shared/api-types";
import {
  previousSoapNoteQueryOptions,
  previousSoapNotesQueryKey,
  previousSoapNotesQueryOptions,
  PreviousSoapNotesScopeChangedError,
  type ClerkGetToken,
} from "../../lib/previous-soap-notes";
import { isNotFoundError } from "../../lib/api";

export type PreviousSoapNoteSelectionView =
  | { status: "loading"; noteId: string }
  | { status: "unavailable"; noteId: string }
  | { status: "ready"; note: AppointmentPreviousSoapNoteContent };

export type PreviousSoapNotesView =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "unavailable" }
  | {
      status: "ready";
      notes: AppointmentPreviousSoapNoteListed[];
      selection: PreviousSoapNoteSelectionView;
    };

export type PreviousSoapNotesSelection =
  | { status: "latest" }
  | {
      status: "older";
      appointmentId: string;
      previousSoapNotesScope: string;
      rdId: string | null;
      noteId: string;
    }
  | {
      status: "requalifying";
      appointmentId: string;
      previousSoapNotesScope: string;
      rdId: string | null;
    };

export type PreviousSoapNotesSelectionAction =
  | {
      type: "select_older";
      appointmentId: string;
      previousSoapNotesScope: string;
      rdId: string | null;
      noteId: string;
    }
  | {
      type: "selection_disproved";
      appointmentId: string;
      previousSoapNotesScope: string;
      rdId: string | null;
      noteId: string;
    }
  | { type: "reset_to_latest" }
  | {
      type: "list_refreshed";
      appointmentId: string;
      previousSoapNotesScope: string;
      rdId: string | null;
    };

export function previousSoapNotesSelectionReducer(
  state: PreviousSoapNotesSelection,
  action: PreviousSoapNotesSelectionAction
): PreviousSoapNotesSelection {
  if (action.type === "reset_to_latest") {
    return { status: "latest" };
  }

  if (action.type === "select_older") {
    return {
      status: "older",
      appointmentId: action.appointmentId,
      previousSoapNotesScope: action.previousSoapNotesScope,
      rdId: action.rdId,
      noteId: action.noteId,
    };
  }

  if (action.type === "selection_disproved") {
    if (!isDisprovedSelection(state, action)) return state;

    return {
      status: "requalifying",
      appointmentId: action.appointmentId,
      previousSoapNotesScope: action.previousSoapNotesScope,
      rdId: action.rdId,
    };
  }

  if (action.type === "list_refreshed") {
    const refreshMatchesScope =
      state.status === "requalifying" &&
      state.appointmentId === action.appointmentId &&
      state.previousSoapNotesScope === action.previousSoapNotesScope;
    const refreshMatchesRd =
      state.status === "requalifying" && state.rdId === action.rdId;

    if (refreshMatchesScope && refreshMatchesRd) return { status: "latest" };
  }

  return state;
}

function isDisprovedSelection(
  state: PreviousSoapNotesSelection,
  action: Extract<PreviousSoapNotesSelectionAction, { type: "selection_disproved" }>
): boolean {
  if (state.status !== "older") return false;

  if (state.appointmentId !== action.appointmentId) return false;

  if (state.previousSoapNotesScope !== action.previousSoapNotesScope) return false;

  if (state.rdId !== action.rdId) return false;

  return state.noteId === action.noteId;
}

type AvailablePreviousSoapNotes = Extract<
  AppointmentPreviousSoapNotesResponse,
  { status: "available" }
>;

function isAvailablePreviousSoapNotes(
  value: AppointmentPreviousSoapNotesResponse | undefined
): value is AvailablePreviousSoapNotes {
  return value?.status === "available";
}

export function usePreviousSoapNotes(input: {
  appointmentId: string;
  previousSoapNotesScope: string;
  rdId?: string;
  getToken: ClerkGetToken;
}) {
  const queryClient = useQueryClient();
  const [selection, dispatchSelection] = useReducer(previousSoapNotesSelectionReducer, {
    status: "latest",
  });
  const queries = usePreviousSoapNotesQueries(input, selection);
  useResetSelection(input, dispatchSelection);
  useDisprovedSelectionRecovery(input, queries, dispatchSelection, queryClient);
  useScopeChangeRecovery(input, queries.scopeChanged, dispatchSelection, queryClient);
  const view = previousSoapNotesView(input, selection, queries);

  return {
    view,
    select(noteId: string) {
      if (noteId === queries.available?.latest.id) {
        dispatchSelection({ type: "reset_to_latest" });

        return;
      }

      dispatchSelection({
        type: "select_older",
        appointmentId: input.appointmentId,
        previousSoapNotesScope: input.previousSoapNotesScope,
        rdId: input.rdId ?? null,
        noteId,
      });
    },
    retry() {
      void queries.list.refetch();
    },
    retrySelection() {
      void queries.selected.refetch();
    },
  };
}

type PreviousSoapNotesInput = Parameters<typeof usePreviousSoapNotes>[0];
type SelectionDispatch = Dispatch<PreviousSoapNotesSelectionAction>;

function usePreviousSoapNotesQueries(
  input: PreviousSoapNotesInput,
  selection: PreviousSoapNotesSelection
) {
  const rdId = input.rdId ?? null;
  const list = useQuery(
    previousSoapNotesQueryOptions(
      input.getToken,
      input.appointmentId,
      input.previousSoapNotesScope,
      undefined,
      rdId
    )
  );
  const listData = list.data;
  const available = isAvailablePreviousSoapNotes(listData) ? listData : null;
  const activeOlderSelection = matchingOlderSelection(selection, input, rdId);
  const scopedNoteId = activeOlderSelection?.noteId ?? null;
  const selectedNoteId = selectedPreviousNoteId(available, scopedNoteId);
  const needsSelectedRead = Boolean(
    available && selectedNoteId && selectedNoteId !== available.latest.id
  );
  const selected = useQuery({
    ...previousSoapNoteQueryOptions(
      input.getToken,
      input.appointmentId,
      input.previousSoapNotesScope,
      selectedNoteId ?? "",
      undefined,
      rdId
    ),
    enabled: needsSelectedRead,
  });
  const selectionDisproved = Boolean(
    activeOlderSelection && selected.isError && isNotFoundError(selected.error)
  );
  const scopeChanged =
    isScopeChangedFailure(list.isError, list.error) ||
    isScopeChangedFailure(selected.isError, selected.error);

  return {
    activeOlderSelection,
    available,
    list,
    listData,
    rdId,
    scopeChanged,
    selected,
    selectedNoteId,
    selectionDisproved,
  };
}

function isScopeChangedFailure(isError: boolean, error: unknown): boolean {
  return isError && error instanceof PreviousSoapNotesScopeChangedError;
}

type PreviousSoapNotesQueries = ReturnType<typeof usePreviousSoapNotesQueries>;

function matchingOlderSelection(
  selection: PreviousSoapNotesSelection,
  input: PreviousSoapNotesInput,
  rdId: string | null
): Extract<PreviousSoapNotesSelection, { status: "older" }> | null {
  if (selection.status !== "older") return null;

  if (selection.appointmentId !== input.appointmentId) return null;

  if (selection.previousSoapNotesScope !== input.previousSoapNotesScope) return null;

  if (selection.rdId !== rdId) return null;

  return selection;
}

function selectedPreviousNoteId(
  available: AvailablePreviousSoapNotes | null,
  scopedNoteId: string | null
): string | null {
  if (!available) return null;

  if (scopedNoteId && available.notes.some((note) => note.id === scopedNoteId)) {
    return scopedNoteId;
  }

  return available.latest.id;
}

function useResetSelection(
  input: PreviousSoapNotesInput,
  dispatchSelection: SelectionDispatch
): void {
  useEffect(() => {
    dispatchSelection({ type: "reset_to_latest" });
  }, [input.appointmentId, input.previousSoapNotesScope, input.rdId]);
}

function useDisprovedSelectionRecovery(
  input: PreviousSoapNotesInput,
  queries: PreviousSoapNotesQueries,
  dispatchSelection: SelectionDispatch,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  useEffect(() => {
    if (!queries.activeOlderSelection || !queries.selectionDisproved) return;

    dispatchSelection({
      type: "selection_disproved",
      appointmentId: input.appointmentId,
      previousSoapNotesScope: input.previousSoapNotesScope,
      rdId: queries.rdId,
      noteId: queries.activeOlderSelection.noteId,
    });
    void queryClient
      .invalidateQueries({
        queryKey: previousSoapNotesQueryKey(
          input.appointmentId,
          input.previousSoapNotesScope,
          queries.rdId
        ),
        exact: true,
      })
      .finally(() => {
        dispatchSelection({
          type: "list_refreshed",
          appointmentId: input.appointmentId,
          previousSoapNotesScope: input.previousSoapNotesScope,
          rdId: queries.rdId,
        });
      });
  }, [
    queries.activeOlderSelection,
    queries.rdId,
    queries.selectionDisproved,
    input.appointmentId,
    input.previousSoapNotesScope,
    queryClient,
  ]);
}

function useScopeChangeRecovery(
  input: PreviousSoapNotesInput,
  scopeChanged: boolean,
  dispatchSelection: SelectionDispatch,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  useEffect(() => {
    if (!scopeChanged) return;

    dispatchSelection({ type: "reset_to_latest" });
    void queryClient.invalidateQueries({
      queryKey: ["appointment", input.appointmentId, input.rdId ?? null],
      exact: true,
    });
  }, [input.appointmentId, input.rdId, queryClient, scopeChanged]);
}

function previousSoapNotesView(
  input: PreviousSoapNotesInput,
  selection: PreviousSoapNotesSelection,
  queries: PreviousSoapNotesQueries
): PreviousSoapNotesView {
  const requalifying = isRequalifyingSelection(selection, input, queries.rdId);
  if (requalifying || queries.selectionDisproved) return { status: "loading" };

  if (queries.scopeChanged || queries.list.isError) return { status: "unavailable" };

  if (!queries.listData) return { status: "loading" };

  if (queries.listData.status === "empty") return { status: "empty" };

  return {
    status: "ready",
    notes: queries.listData.notes,
    selection: selectedNoteView(queries.listData, queries),
  };
}

function isRequalifyingSelection(
  selection: PreviousSoapNotesSelection,
  input: PreviousSoapNotesInput,
  rdId: string | null
): boolean {
  if (selection.status !== "requalifying") return false;

  if (selection.appointmentId !== input.appointmentId) return false;

  if (selection.previousSoapNotesScope !== input.previousSoapNotesScope) return false;

  return selection.rdId === rdId;
}

function selectedNoteView(
  listData: AvailablePreviousSoapNotes,
  queries: PreviousSoapNotesQueries
): PreviousSoapNoteSelectionView {
  const selectedNote =
    queries.selectedNoteId === listData.latest.id
      ? listData.latest
      : queries.selected.data;
  if (selectedNote) return { status: "ready", note: selectedNote };

  const noteId = queries.selectedNoteId ?? listData.latest.id;
  if (queries.selected.isError) return { status: "unavailable", noteId };

  return { status: "loading", noteId };
}
